import React, { useState, useEffect } from 'react';
import { CommentSection } from 'react-comments-section';
import 'react-comments-section/dist/index.css';
import 'react-notifications/lib/notifications.css';
import {NotificationContainer, NotificationManager} from 'react-notifications';

const MyCommentsSection = ({ postId, userId, logComment }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loggedUserData, setLoggedUserData] = useState(null);

  const encodeUserId = (userId) => {
    const salt = "6312241f4b246734bbfd5e9ed9120cd7fb64dc6020ef66c2e7f124a5bb1d3d25";
    const encodedId = btoa(userId + salt); // Base64 encode the user ID with the salt appended
    return encodedId;
  };

  const fetchUserData = async (userId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/users/${userId}`);
      console.log(response);
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching user data:", error);
      return null; // Return null if there's an error
    }
  };

  const fetchLoggedUserData = async () => {
    try {
      const data = await fetchUserData(userId);
      setLoggedUserData(data);
    } catch (error) {
      console.error("Error fetching logged-in user data:", error);
    }
  };

  const fetchCommentsWithUserData = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/comments/post/${postId}`);
      const data = await response.json();

      const commentData = await Promise.all(data.map(async (comment) => {
        const userData = await fetchUserData(comment.user_id);
        return {
          comId: comment.id.toString(),
          userId: comment.user_id,
          avatarUrl: userData?.profile_picture, // Placeholder image
          fullName: userData?.username || `User${comment.user_id}`, // Placeholder name
          text: comment.text,
          createdAt: comment.created_at,
          replies: [],
          parent_id: comment.parent_id,
        };
      }));

      // Nest replies under their parent comments
      const nestedComments = nestReplies(commentData);
      setComments(nestedComments);
    } catch (error) {
      console.error("Error fetching comments:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCommentsWithUserData();
    fetchLoggedUserData(); // Fetch logged user data when component mounts
  }, [postId]);

  const nestReplies = (comments, reply = null) => {
    if (reply) {
      return comments.map(comment => {
        if (comment.comId === reply.parent_id) {
          return {
            ...comment,
            replies: [...comment.replies, reply],
          };
        } else if (comment.replies && comment.replies.length > 0) {
          return {
            ...comment,
            replies: nestReplies(comment.replies, reply),
          };
        } else {
          return comment;
        }
      });
    } else {
      const commentMap = {};
      comments.forEach(comment => {
        commentMap[comment.comId] = { ...comment, replies: [] };
      });

      const nestedComments = [];
      comments.forEach(comment => {
        if (comment.parent_id) {
          if (commentMap[comment.parent_id]) {
            commentMap[comment.parent_id].replies.push(commentMap[comment.comId]);
          }
        } else {
          nestedComments.push(commentMap[comment.comId]);
        }
      });

      return nestedComments;
    }
  };

  const handleAddComment = (commentData) => {

    if (userId === "ANONYMOUS") {
      NotificationManager.error('Trebuie să fi conectat pentru a putea comenta la postare.');
      return;
    }

    const newComment = {
      post_id: postId,
      user_id: userId,
      text: commentData.text,
      parent_id: commentData.parentId || null,
    };

    fetch(`${process.env.REACT_APP_API_URL}/comments`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(newComment),
    })
      .then(response => response.json())
      .then(() => {
        fetchCommentsWithUserData(); // Refresh comments after adding a new one
      })
      .catch(error => {
        console.error("Error adding comment:", error);
      });

    logComment();
  };

  const handleDeleteComment = (commentId) => {
    fetch(`${process.env.REACT_APP_API_URL}/comments/${commentId}`, {
      method: 'DELETE',
    })
      .then(() => {
        fetchCommentsWithUserData(); // Refresh comments after deleting one
      })
      .catch(error => {
        console.error("Error deleting comment:", error);
      });
  };

  const handleEditComment = (commentId, updatedText) => {
    const updatedComment = {
      text: updatedText,
    };

    fetch(`${process.env.REACT_APP_API_URL}/comments/${commentId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updatedComment),
    })
      .then(() => {
        fetchCommentsWithUserData(); // Refresh comments after editing one
      })
      .catch(error => {
        console.error("Error updating comment:", error);
      });
  };

  const handleReplyComment = (commentData) => {
    let text = commentData.text;
    if (commentData.repliedToCommentId) {
      let allComments = [];
      comments.forEach(comment => {
        allComments.push(comment);
        if (comment.replies) {
          comment.replies.forEach(reply => allComments.push(reply));
        }
      });

      const repliedToComment = allComments.find(comment => comment.comId === commentData.repliedToCommentId);
      if (repliedToComment) {
        const encodedUserId = encodeUserId(repliedToComment.userId);
        const url = `https://4construct.ro/homepage/index.php?p=utilizator&id=${encodedUserId}=`;
        text = `<a href="${url}">@${repliedToComment.fullName}</a> ${text}`;
      }
    }

    const newReply = {
      post_id: postId,
      user_id: userId,
      text: text,
      parent_id: commentData.parentOfRepliedCommentId || commentData.repliedToCommentId, // Ensure this is the ID of the parent comment
    };

    fetch(`${process.env.REACT_APP_API_URL}/comments`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(newReply),
    })
      .then(response => response.json())
      .then(() => {
        fetchCommentsWithUserData(); // Refresh comments after adding a reply
      })
      .catch(error => {
        console.error("Error adding reply:", error);
      });
  };

  if (!loggedUserData) {
    fetchLoggedUserData(); // Fetch user data if not already fetched
    return <div>Loading...</div>;
  }

  return (
    <div>
      {loading ? (
        <p>Loading comments...</p>
      ) : (
        <>
          <CommentSection
            currentUser={{
              currentUserId: userId,
              currentUserImg: userId != "ANONYMOUS" ? loggedUserData.profile_picture : "https://4construct.ro/homepage/dt/assets/img/useri/farapoza.png",
              currentUserFullName: loggedUserData.username,
            }}
            logIn={{ loginLink: "http://localhost:3000/login", signupLink: "http://localhost:3000/signup" }}
            commentData={comments}
            onSubmitAction={handleAddComment}
            onDeleteAction={handleDeleteComment}
            onEditAction={handleEditComment}
            onReplyAction={handleReplyComment}
            titleStyle={{ display: "none" }}
            submitBtnStyle={{ text: "btn btn-primary" }}
            advancedInput={true}
            customNoComment={() => <p style={{ textAlign: "center" }}>Nu exista comentarii</p>}
          />
          <NotificationContainer />
        </>
      )}
    </div>
  );
};

export default MyCommentsSection;
